import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, IconButton } from '@mui/material';
import { getKioskPinStateRequest } from 'api/requests/profile-requests';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  PROFILE__PINCODE_ADD,
  PROFILE__PINCODE_CHANGE,
  PROFILE__PINCODE_NOT_SET,
  PROFILE__PINCODE_TITLE,
} from 'translations/constants';
import IKioskPinState from 'types/profile/IKioskPinState';
import RevealPincodeModal from './RevealPincodeModal';
import {
  PincodeSectionLabel,
  PincodeSectionLink,
  PincodeSectionMuted,
  PincodeSectionRow,
  PincodeSectionValue,
} from './styles';

const REVEAL_DURATION_MS = 30_000;

const PincodeSection = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const [pinState, setPinState] = useState<IKioskPinState | null>(null);
  const [hidden, setHidden] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const remaskTimer = useRef<number | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const result = await getKioskPinStateRequest();
        if (mounted) setPinState(result);
      } catch {
        // 403 (not an employee) or network error — hide the section entirely rather than break the page.
        if (mounted) setHidden(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(
    () => () => {
      if (remaskTimer.current) window.clearTimeout(remaskTimer.current);
    },
    [],
  );

  const handleRevealed = (pin: string) => {
    if (remaskTimer.current) window.clearTimeout(remaskTimer.current);
    setPlaintext(pin);
    setRevealOpen(false);
    remaskTimer.current = window.setTimeout(() => setPlaintext(null), REVEAL_DURATION_MS);
  };

  // Hiding an already-revealed pin is free — only revealing it requires the password.
  const handleToggleReveal = () => {
    if (plaintext) {
      if (remaskTimer.current) window.clearTimeout(remaskTimer.current);
      setPlaintext(null);
      return;
    }
    setRevealOpen(true);
  };

  const goToPincodePage = () => navigate('/pincode', { state: pinState ?? undefined });

  if (hidden || pinState === null) return null;

  return (
    <Box mt={2} mb={2}>
      <PincodeSectionLabel>{t(PROFILE__PINCODE_TITLE)}</PincodeSectionLabel>
      <PincodeSectionRow>
        {pinState.hasPin ? (
          <>
            <PincodeSectionValue>{plaintext ?? '•••••'}</PincodeSectionValue>
            <IconButton aria-label="reveal-pincode" size="small" onClick={handleToggleReveal}>
              {plaintext ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </IconButton>
            <PincodeSectionLink component="button" type="button" onClick={goToPincodePage}>
              {t(PROFILE__PINCODE_CHANGE)}
            </PincodeSectionLink>
          </>
        ) : (
          <>
            <PincodeSectionMuted>{t(PROFILE__PINCODE_NOT_SET)}</PincodeSectionMuted>
            <PincodeSectionLink component="button" type="button" onClick={goToPincodePage}>
              {t(PROFILE__PINCODE_ADD)}
            </PincodeSectionLink>
          </>
        )}
      </PincodeSectionRow>

      <RevealPincodeModal open={revealOpen} onClose={() => setRevealOpen(false)} onRevealed={handleRevealed} />
    </Box>
  );
};

export default PincodeSection;
